import { bayhiveDialogInitialFocus } from '../../Components/ShopManagement/dialogInitialFocus.js';
import { bayhiveDialogStableSize } from '../../Components/ShopManagement/dialogStableSize.js';

const credentialAutocompleteTokens = new Set([
  'username',
  'current-password',
  'new-password',
  'one-time-code',
]);

const bayhiveBrowserSuggestions = ({ attrs = {}, parent }) => {
  // Password forwards its autocomplete hint on the parent rather than the nested InputText.
  const autocomplete = String(attrs.autocomplete ?? parent?.attrs?.autocomplete ?? '').trim();
  if (parent?.instance?.$name === 'Password' || attrs.type === 'password') {
    return { autocomplete: autocomplete || 'on' };
  }

  const tokens = autocomplete.toLowerCase().split(/\s+/);
  const allowAutofill = tokens.some(
    (token) =>
      credentialAutocompleteTokens.has(token) || token === 'billing' || token.startsWith('cc-'),
  );

  return { autocomplete: allowAutofill ? autocomplete : 'off' };
};

const resolveBayhiveFieldOverlaySize = ({ instance, props }) => {
  const dataTable = instance?.$el?.closest?.('.p-datatable');
  const fieldSize = props?.size || instance?.$props?.size;

  if (dataTable?.classList.contains('p-datatable-sm')) {
    return 'small';
  }

  if (dataTable?.classList.contains('p-datatable-lg')) {
    return 'large';
  }

  if (dataTable) {
    return 'medium';
  }

  if (fieldSize === 'small' || fieldSize === 'large') {
    return fieldSize;
  }

  return 'medium';
};

const bayhiveFieldOverlay = (options) => ({
  class: [
    'bayhive-field-overlay',
    `bayhive-field-overlay--${resolveBayhiveFieldOverlaySize(options)}`,
  ],
});

const isUnmodifiedEnterKey = (event) =>
  event.key === 'Enter' &&
  !event.altKey &&
  !event.ctrlKey &&
  !event.metaKey &&
  !event.shiftKey &&
  !event.repeat &&
  !event.isComposing;

const activateChoiceOnEnter = (event) => {
  const input = event.currentTarget;

  if (!isUnmodifiedEnterKey(event) || input?.disabled || input?.readOnly) {
    return;
  }

  event.preventDefault();
  input.click();
};

const datePickerInputPassThrough = ({ parent }) => ({
  onKeydown: (event) => {
    const datePicker = parent?.instance;

    if (
      !isUnmodifiedEnterKey(event) ||
      datePicker?.overlayVisible ||
      event.currentTarget?.getAttribute('role') !== 'combobox'
    ) {
      return;
    }

    event.preventDefault();
    datePicker?.onButtonClick?.();
  },
});

const syncBayhiveDialogScrollEdges = (content) => {
  const dialog = content?.closest?.('.p-dialog');
  if (!dialog) return;

  const maxScroll = Math.max(0, content.scrollHeight - content.clientHeight);
  const hasOverflow = maxScroll > 1;
  dialog.toggleAttribute('data-scroll-above', hasOverflow && content.scrollTop > 1);
  dialog.toggleAttribute('data-scroll-below', hasOverflow && content.scrollTop < maxScroll - 1);
};

const scheduleBayhiveDialogScrollEdgeSync = (content) => {
  syncBayhiveDialogScrollEdges(content);
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => syncBayhiveDialogScrollEdges(content));
  }
};

const bayhiveDialogContentPassThrough = {
  onScroll: (event) => syncBayhiveDialogScrollEdges(event.currentTarget),
  onVnodeMounted: (vnode) => scheduleBayhiveDialogScrollEdgeSync(vnode.el),
  onVnodeUpdated: (vnode) => scheduleBayhiveDialogScrollEdgeSync(vnode.el),
};

/**
 * PrimeVue pass-through options shared by every BayHive entry (the full app shell and the
 * lean auth shell), so field overlays, dialogs and form error navigation behave the same.
 *
 * @param {object} formErrorNavigation the value returned by installBayhiveFormErrorNavigation()
 */
export const createBayhivePrimeVuePassThrough = (formErrorNavigation) => ({
  autocomplete: {
    dropdown: {
      'aria-label': 'Show suggestions',
      tabindex: -1,
    },
    overlay: bayhiveFieldOverlay,
  },
  checkbox: {
    input: {
      onKeydown: activateChoiceOnEnter,
    },
  },
  cascadeselect: {
    overlay: bayhiveFieldOverlay,
  },
  datepicker: {
    pcInputText: {
      root: datePickerInputPassThrough,
    },
    panel: bayhiveFieldOverlay,
  },
  datatable: {
    bodyRow: ({ props }) => ({
      'data-bayhive-row-key':
        typeof props.dataKey === 'string' ? props.rowData?.[props.dataKey] : undefined,
    }),
  },
  dialog: {
    ...bayhiveDialogInitialFocus,
    root: {
      ...bayhiveDialogInitialFocus.root,
      onVnodeUnmounted: (vnode) => {
        bayhiveDialogStableSize.root.onVnodeUnmounted(vnode);
        bayhiveDialogInitialFocus.root.onVnodeUnmounted(vnode);
      },
    },
    transition: (options) => {
      const initialFocusTransition = bayhiveDialogInitialFocus.transition(options);
      return {
        ...initialFocusTransition,
        onEnter: (dialog) => {
          initialFocusTransition.onEnter?.(dialog);
          bayhiveDialogStableSize.transition.onEnter(dialog);
        },
        onBeforeLeave: (dialog) => {
          bayhiveDialogStableSize.transition.onBeforeLeave(dialog);
          initialFocusTransition.onBeforeLeave?.(dialog);
        },
      };
    },
    content: bayhiveDialogContentPassThrough,
  },
  form: {
    root: {
      ...formErrorNavigation,
      autocomplete: 'off',
    },
  },
  // Composite fields (InputNumber, Password, DatePicker and picker filters) also use InputText.
  inputtext: {
    root: bayhiveBrowserSuggestions,
  },
  inputmask: {
    root: bayhiveBrowserSuggestions,
  },
  multiselect: {
    overlay: bayhiveFieldOverlay,
  },
  select: {
    overlay: bayhiveFieldOverlay,
  },
  treeselect: {
    panel: bayhiveFieldOverlay,
  },
  textarea: {
    root: bayhiveBrowserSuggestions,
  },
  toggleswitch: {
    input: {
      onKeydown: activateChoiceOnEnter,
    },
  },
});
